import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { TenantData, tenantService } from '../services/tenantService';
import { checkAuthAndRedirect } from '../services/authUtils';

interface Message {
  text: string;
  isUser: boolean;
}

interface ChatBotProps {
  tenantcode: string;
  tenants: TenantData[];
  onTenantChange: (tenantcode: string) => void;
  onTenantDeleted: () => void;
}

export const ChatBot: React.FC<ChatBotProps> = ({ tenantcode, tenants, onTenantChange, onTenantDeleted }) => {
  const [messages, setMessages] = useState<Message[]>([
    { text: "Hello! How can I help you today?", isUser: false }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = { text: inputText, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      const isAuthenticated = await checkAuthAndRedirect();
      if (!isAuthenticated) {
        setMessages(prev => [...prev, { text: 'Authentication required. Please login again.', isUser: false }]);
        setIsLoading(false);
        return;
      }
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const apiUrl = process.env.REACT_APP_LAMBDA_API_ENDPOINT?.replace('/upload', '');
      
      const response = await fetch(apiUrl!, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ query: inputText, tenantcode: tenantcode })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Sorry, something went wrong.';
        
        if (errorText.includes('bedrock') || errorText.includes('image')) {
          errorMessage = 'Unable to process your request. Please try uploading files without images for better results.';
        } else if (errorText.includes('knowledgebase') || errorText.includes('knowledge')) {
          errorMessage = 'Knowledge base error. Please try again or contact support.';
        }
        
        setMessages(prev => [...prev, { text: errorMessage, isUser: false }]);
        return;
      }
      
      let data = await response.text();
      
      // Remove surrounding quotes if present
      if (data.startsWith('"') && data.endsWith('"')) {
        data = data.slice(1, -1);
      }
      
      // Process escape sequences
      data = data.replace(/\\n/g, '\n').replace(/\\"/g, '"');
      
      setMessages(prev => [...prev, { text: data || 'Sorry, I could not process your request.', isUser: false }]);
    } catch (error) {
      setMessages(prev => [...prev, { text: 'Sorry, something went wrong.', isUser: false }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!tenantcode || !window.confirm(`Are you sure you want to delete tenant "${tenantcode}"? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await tenantService.deleteTenant(tenantcode);
      onTenantDeleted();
      setMessages([{ text: "Hello! How can I help you today?", isUser: false }]);
    } catch (error) {
      console.error('Error deleting tenant:', error);
      alert('Failed to delete tenant. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 w-full h-[700px] flex flex-col">
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold text-[#6B3FA0] mb-3">Chat Assistant</h3>
        {tenants.length > 0 && (
          <div className="flex gap-2">
            <select
              value={tenantcode}
              onChange={(e) => onTenantChange(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6B3FA0]"
            >
              <option value="">Select knowledge base...</option>
              {tenants.map((tenant) => (
                <option key={tenant.tenantcode} value={tenant.tenantcode}>
                  Tenant: {tenant.tenantcode} ({tenant.fileCount} files)
                </option>
              ))}
            </select>
            {tenantcode && (
              <button
                onClick={handleDeleteTenant}
                disabled={isDeleting}
                className="px-2 py-2 text-sm bg-white text-red-500 border border-red-500 rounded-md hover:bg-red-50 disabled:opacity-50 flex items-center justify-center"
                title={isDeleting ? 'Deleting...' : `Delete Tenant: ${tenantcode}`}
              >
                {isDeleting ? '...' : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6H5H21" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 11V17" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 11V17" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            )}
          </div>
        )}
      </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {messages.map((message, index) => (
            <div key={index} className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs px-3 py-2 rounded-lg ${
                message.isUser 
                  ? 'bg-[#6B3FA0] text-white' 
                  : 'bg-gray-100 text-gray-800'
              }`} style={{lineHeight: '1.5'}}>
                {message.isUser ? (
                  message.text
                ) : (
                  <div dangerouslySetInnerHTML={{
                    __html: message.text
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                      .replace(/^[•\-\*]\s+(.+)$/gm, '<li>$1</li>')
                      .replace(/(<li>.*<\/li>)/s, '<ul style="margin: 8px 0; padding-left: 20px;">$1</ul>')
                      .split('\n\n')
                      .map(paragraph => {
                        if (paragraph.includes('<ul>')) return paragraph;
                        return `<p style="margin-bottom: 8px;">${paragraph.replace(/\n/g, '<br/>')}</p>`;
                      })
                      .join('')
                  }} />
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-800 px-3 py-2 rounded-lg">
                Typing...
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t">
          <div className="flex space-x-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6B3FA0]"
            />
            <button
              onClick={sendMessage}
              disabled={isLoading}
              className="px-4 py-2 bg-[#6B3FA0] text-white rounded-md hover:bg-[#4E2E77] disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
    </div>
  );
};